import { Box, Container, Grid, Paper, Stack, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { m } from 'framer-motion';
import AnimatedCounter from 'src/components/custom/AnimationCounter';
import { useResponsive } from 'src/hooks/use-responsive';
import { useLocales } from 'src/locales';
import { secondaryFont } from 'src/theme/typography';

const StyledTextGradient = styled(m.h1)(({ theme }) => ({
  background: '-webkit-linear-gradient(0deg, #164732, #51B949)',
  '-webkit-background-clip': 'text',
  '-webkit-text-fill-color': 'transparent',
  textTransform: 'uppercase',
  fontSize: `35px`,
  fontFamily: secondaryFont,
}));

const StyledRectangle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 10,
  height: 66,
  background: '#EDFFEB',
  borderTopLeftRadius: '8px',
  borderBottomLeftRadius: '8px',
}));

function LiquidityView() {
  const { t } = useLocales();
  const theme = useTheme();

  const mdDown = useResponsive('down', 'md');

  const config = [
    {
      title: t('string_076'),
      description: t('string_077'),
      color: '#EDFFEB',
    },
    {
      title: t('string_078'),
      description: t('string_079'),
      color: '#DAFFD7',
    },
    {
      title: t('string_080'),
      description: t('string_081'),
      color: '#C8FFC4',
    },
    {
      title: t('string_082'),
      description: t('string_083'),
      color: '#B6FFB0',
    },
  ];

  return (
    <Container>
      <Stack py={mdDown ? 6 : 12}>
        <StyledTextGradient sx={{ fontSize: mdDown ? 20 : 25, textAlign: 'center' }}>
          {t('string_061')}
        </StyledTextGradient>

        <Box component="img" srcSet="/assets/images/liquidity/liquidity-001.png 2x" alt="" py={6} />

        <Typography
          fontSize={mdDown ? 25 : 30}
          textAlign="center"
          sx={{
            '& > span': {
              color: theme.palette.primary.main,
            },
          }}
        >
          <span>{t('string_062.1')}</span> {t('string_062.2')} <span>{t('string_062.3')}</span>
          <br />
          {t('string_062.4')}
        </Typography>

        <Grid container justifyContent="center" mt={6}>
          <Grid item xs={12} sm={8} md={4}>
            <Stack>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <AnimatedCounter
                  target={90}
                  sx={{
                    fontSize: mdDown ? 60 : 70,
                    fontWeight: '800',
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: mdDown ? 60 : 70,
                    fontWeight: '800',
                    color: theme.palette.primary.main,
                  }}
                >
                  +
                </Typography>
              </Stack>
              <Typography fontSize={20} textAlign="center">
                {t('string_063')}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <Stack>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <AnimatedCounter
                  target={1}
                  sx={{
                    fontSize: mdDown ? 60 : 70,
                    fontWeight: '800',
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: mdDown ? 60 : 70,
                    fontWeight: '800',
                    color: theme.palette.primary.main,
                  }}
                >
                  M+
                </Typography>
              </Stack>
              <Typography fontSize={20} textAlign="center">
                {t('string_064')}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <Stack>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <Typography
                  sx={{ fontSize: 70, fontWeight: '800', color: theme.palette.primary.main }}
                >
                  X
                </Typography>
                <AnimatedCounter
                  target={3}
                  sx={{ fontSize: 70, fontWeight: '800', color: theme.palette.primary.main }}
                />
              </Stack>
              <Typography fontSize={20} textAlign="center">
                {t('string_065')}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Stack
          mt={12}
          component={Paper}
          p={4}
          direction={mdDown ? 'column' : 'row'}
          alignItems="center"
          justifyContent="center"
          spacing={6}
          sx={{ background: '#F5FFF4', borderRadius: '30px' }}
        >
          <Stack>
            <Typography fontSize={mdDown ? 30 : 40}>{t('string_066')}</Typography>
            <StyledTextGradient sx={{ fontSize: mdDown ? 40 : 50, my: 0 }}>
              {t('string_067')}
            </StyledTextGradient>
            <Typography>{t('string_068')}</Typography>
          </Stack>

          <Box
            component="img"
            srcSet="/assets/images/liquidity/liquidity-002.png 2x"
            alt=""
            maxWidth={384}
            width={mdDown ? '90%' : 'auto'}
          />
        </Stack>

        <Grid container justifyContent="center" spacing={3} pt={12}>
          <Grid item xs={12} sm={8} md={4}>
            <Stack
              sx={{
                position: 'relative',
                height: '100%',
                border: '1px solid #E0E0E0',
                background: '#FAF9F9',
                p: 2,
              }}
            >
              <StyledRectangle sx={{ top: '10%', left: '-11px' }} />
              <StyledTextGradient sx={{ fontSize: 20, textAlign: 'center' }}>
                {t('string_069')}
              </StyledTextGradient>
              <Typography fontSize={16} textAlign="center">
                {t('string_070')}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <Stack
              sx={{
                position: 'relative',
                height: '100%',
                border: '1px solid #E0E0E0',
                background: '#FAF9F9',
                p: 2,
              }}
            >
              <StyledRectangle
                sx={{
                  top: '50%',
                  left: '-11px',
                  transform: 'translateY(-50%)',
                  background: '#B8FFB1',
                }}
              />
              <StyledTextGradient sx={{ fontSize: 20, textAlign: 'center' }}>
                {t('string_071')}
              </StyledTextGradient>
              <Typography fontSize={16} textAlign="center">
                {t('string_072')}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <Stack
              sx={{
                position: 'relative',
                height: '100%',
                border: '1px solid #E0E0E0',
                background: '#FAF9F9',
                p: 2,
              }}
            >
              <StyledRectangle sx={{ bottom: '10%', left: '-11px', background: '#75FF68' }} />
              <StyledTextGradient sx={{ fontSize: 20, textAlign: 'center' }}>
                {t('string_073')}
              </StyledTextGradient>
              <Typography fontSize={16} textAlign="center">
                {t('string_074')}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Grid container alignItems="center" justifyContent="center" py={12} spacing={6}>
          <Grid item xs={12} sm={8} md={6}>
            <Typography fontSize={mdDown ? 35 : 50} textAlign={mdDown ? 'center' : 'left'}>
              {t('string_075')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={6}>
            <Stack>
              {config.map((x, i) => (
                <Stack
                  component={Paper}
                  key={i}
                  sx={{
                    background: x.color,
                    p: 4,
                    mb: -18,
                    borderRadius: '30px',
                    transition: theme.transitions.create('all'),
                    '&:hover': {
                      mb: i === 3 ? -18 : 2,
                    },
                  }}
                >
                  <StyledTextGradient sx={{ fontSize: 20 }}>{x.title}</StyledTextGradient>
                  <Typography fontSize={18}>{x.description}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <StyledTextGradient sx={{ fontSize: mdDown ? 40 : 55, textAlign: 'center', pt: 6 }}>
          {t('string_084')}
        </StyledTextGradient>
      </Stack>
    </Container>
  );
}

export default LiquidityView;
